import React, { useState, useEffect } from "react";
import { Question } from "../../types/quiz";
import "./QuestionCard.css";

interface QuestionCardProps {
  question: Question;
  selectedAnswers: number[];
  onAnswerSelect: (index: number) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  selectedAnswers,
  onAnswerSelect,
}) => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
    return () => setAnimateIn(false);
  }, [question.id]);

  const getOptionClassName = (optionIndex: number) => {
    return selectedAnswers.includes(optionIndex)
      ? "option option-selected"
      : "option";
  };

  const handleOptionClick = (optionIndex: number) => {
    onAnswerSelect(optionIndex);
  };

  return (
    <div className={`question-card ${animateIn ? "animate-in" : ""}`}>
      <div className="question-header">
        <div className="question-badges">
          <span className="question-category">{question.category}</span>
          <span className="question-type">
            {question.type === "multiple" ? (
              <>
                <svg className="icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 8a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 12a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Multiple Choice
              </>
            ) : (
              <>
                <svg className="icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Single Choice
              </>
            )}
          </span>
        </div>
      </div>

      <div className="question-content">
        <h3 className="question-text">{question.question}</h3>

        {question.type === "multiple" && (
          <div className="multiple-choice-hint">
            <svg className="hint-icon" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <span>You can select multiple options</span>
          </div>
        )}
      </div>

      <div className="options-container">
        {question.options.map((option, index) => {
          const isSelected = selectedAnswers.includes(index);
          return (
            <button
              key={index}
              className={getOptionClassName(index)}
              onClick={() => handleOptionClick(index)}
              style={{ animationDelay: `${index * 0.1}s` }}
              aria-pressed={isSelected}
            >
              <div className="option-indicator">
                {question.type === "multiple" ? (
                  <div className={`checkbox ${isSelected ? "checked" : ""}`}>
                    {isSelected && (
                      <svg
                        className="check-icon"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div className={`radio ${isSelected ? "checked" : ""}`}>
                    {isSelected && <div className="radio-dot" />}
                  </div>
                )}
              </div>
              <span className="option-text">{option}</span>
              {isSelected && (
                <div className="selection-indicator">
                  <svg
                    className="selected-icon"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {question.explanation && (
        <div className="explanation">
          <div className="explanation-header">
            <svg
              className="explanation-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <span>Why we ask this</span>
          </div>
          <p className="explanation-text">{question.explanation}</p>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
